/**
 * EL MURO DE MEMES.
 *
 * Javier: «en accesos rápidos el primer bloque que diga memes, estos viven por
 * un día, y se pueden comentar y reaccionar, es para toda la comunidad».
 *
 * ES LO ÚNICO DE LA APLICACIÓN QUE NO ES UN PARTIDO, y esa es la gracia: un
 * lugar donde la gente del tenis tico se ríe junta. Por eso esta pantalla no
 * pide club, no filtra por categoría y no tiene ranking a la vista.
 *
 * LA CLÁUSULA SE LEE ANTES DE PUBLICAR, no después. Está arriba del formulario
 * y no escondida en un enlace: una regla que hay que ir a buscar es una regla
 * que nadie leyó, y el día que alguien reclame va a tener razón.
 *
 * «LE QUEDAN N HORAS» EN CADA UNO. Sin eso, «viven un día» es una promesa que
 * la pantalla no muestra, y el primero al que se le desaparece un meme va a
 * creer que se lo bajaron.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MEME_LIMITS, MEME_RULE_ES, type MatchComment, type Meme } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { AppLayout } from '../components/AppLayout';
import { Avatar } from '../components/Avatar';
import { BackButton } from '../components/BackButton';
import { CommentThread, Reactions } from '../components/Conversation';
import { ImagePicker } from '../components/ImagePicker';
import { Paginador } from '../components/Paginado';
import { api, assetUrl } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { useAnotarVisita } from '../hooks/useAnotarVisita';

export function MemesPage(): JSX.Element {
  // El globito del inicio baja al entrar acá. Ver `useAnotarVisita`.
  useAnotarVisita('memes');
  const { t } = useTranslation();
  const [memes, setMemes] = useState<Meme[]>([]);
  const [páginas, setPáginas] = useState(1);
  const [página, setPágina] = useState(1);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(
    async (cuál: number) => {
      setCargando(true);
      setError(null);
      try {
        const respuesta = await api.listMemes(cuál);
        /*
         * LA LISTA, DEFENDIDA. `Meme[]` es una promesa sobre el contrato, no
         * una garantía sobre el cable: un servidor una versión atrás contesta
         * cualquier cosa en esta ruta nueva, y `.map` sobre lo que no es un
         * arreglo tira la pantalla entera. Ya pasó con la Actividad.
         */
        setMemes(Array.isArray(respuesta.memes) ? respuesta.memes : []);
        setPáginas(typeof respuesta.pages === 'number' ? Math.max(1, respuesta.pages) : 1);
        setPágina(typeof respuesta.page === 'number' ? respuesta.page : cuál);
      } catch (caught) {
        setError(errorMessage(t, caught));
      } finally {
        setCargando(false);
      }
    },
    [t],
  );

  useEffect(() => {
    void cargar(1);
  }, [cargar]);

  /** Reemplaza UNO en la lista, sin recargar la página entera. */
  function reemplazar(meme: Meme): void {
    setMemes((antes) => antes.map((m) => (m.id === meme.id ? meme : m)));
  }


  return (
    <AppLayout>
      <BackButton to="/" />
      <h1>{t('memes.title')}</h1>
      <p className="lead">{t('memes.lead')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <Publicar onPublished={() => void cargar(1)} />

      {cargando ? (
        <p className="help">{t('common.loading')}</p>
      ) : memes.length === 0 ? (
        // VACÍO SE DICE. «No hay ninguno» y «esto no cargó» se ven igual si la
        // lista simplemente no aparece.
        <p className="help">{t('memes.empty')}</p>
      ) : (
        <ul className="meme-list">
          {memes.map((meme) => (
            <li key={meme.id}>
              <MemeCard
                meme={meme}
                onChanged={reemplazar}
                onRemoved={() => void cargar(página)}
              />
            </li>
          ))}
        </ul>
      )}

      <Paginador
        pagina={página}
        paginas={páginas}
        irA={(cuál) => void cargar(cuál)}
        de={t('memes.title')}
      />
    </AppLayout>
  );
}

/**
 * PUBLICAR. La imagen es obligatoria; el pie no.
 *
 * Un meme sin imagen es un mensaje, y para mandar mensajes están los
 * comentarios de los partidos. Sin esa regla, el muro se convierte en un chat
 * sin moderación.
 */
function Publicar({ onPublished }: { onPublished: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [abierto, setAbierto] = useState(false);
  const [image, setImage] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [mandando, setMandando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function publicar(): Promise<void> {
    if (image === null || mandando) return;
    setMandando(true);
    setError(null);
    try {
      await api.createMeme(image, caption);
      setImage(null);
      setCaption('');
      setAbierto(false);
      onPublished();
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setMandando(false);
    }
  }

  if (!abierto) {
    return (
      <p className="button-row">
        <button type="button" onClick={() => setAbierto(true)}>
          {t('memes.publish')}
        </button>
      </p>
    );
  }

  return (
    <section className="meme-form">
      {/* LA CLÁUSULA, ARRIBA Y A LA VISTA. Una regla que hay que ir a buscar
          es una regla que nadie leyó. Sale del contrato y no del catálogo de
          textos: es una regla del producto, y traducida a nueve idiomas serían
          nueve reglas que se pueden separar. */}
      <p className="help meme-rule">{MEME_RULE_ES}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <ImagePicker
        id="meme-image"
        label={t('memes.image')}
        value={image}
        onChange={setImage}
        disabled={mandando}
      />

      <div className="field">
        <label htmlFor="meme-caption">{t('memes.caption')}</label>
        <input
          id="meme-caption"
          type="text"
          autoComplete="off"
          maxLength={MEME_LIMITS.captionMax}
          placeholder={t('memes.captionPlaceholder')}
          value={caption}
          disabled={mandando}
          onChange={(e) => setCaption(e.target.value)}
        />
      </div>

      <div className="button-row">
        <button type="button" disabled={image === null || mandando} onClick={() => void publicar()}>
          {mandando ? t('common.sending') : t('memes.publish')}
        </button>
        <button
          type="button"
          className="link"
          disabled={mandando}
          onClick={() => {
            setAbierto(false);
            setImage(null);
            setError(null);
          }}
        >
          {t('common.cancel')}
        </button>
      </div>
    </section>
  );
}

function MemeCard({
  meme,
  onChanged,
  onRemoved,
}: {
  meme: Meme;
  onChanged: (meme: Meme) => void;
  onRemoved: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [reportando, setReportando] = useState(false);
  /** El cajón de la razón, cerrado hasta que alguien toca «reportar». */
  const [abriendoReporte, setAbriendoReporte] = useState(false);
  const [razón, setRazón] = useState('');

  async function hacer(qué: () => Promise<void>): Promise<void> {
    setError(null);
    try {
      await qué();
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  return (
    <article className="meme-card">
      <header className="meme-head">
        <Avatar url={assetUrl(meme.authorAvatarPath)} name={meme.authorName} size={32} />
        <span className="meme-who">{meme.authorName}</span>
        <span className="help meme-left">{quedan((k, d) => t(k, d ?? {}), meme.expiresAt)}</span>
      </header>

      <img className="meme-image" src={assetUrl(meme.imagePath) ?? ''} alt={meme.caption ?? ''} />

      {meme.caption !== null && <p className="meme-caption">{meme.caption}</p>}

      {error !== null && <Alert kind="error">{error}</Alert>}

      <Reactions
        reactions={meme.reactions}
        onToggle={(emoji, on) =>
          void hacer(async () => onChanged(await api.reactToMeme(meme.id, emoji, on)))
        }
      />

      <CommentThread
        id={meme.id}
        comments={meme.comments}
        onComment={(body) => hacer(async () => onChanged(await api.commentMeme(meme.id, body)))}
        onDelete={(comentario: MatchComment) =>
          hacer(async () => {
            await api.removeMemeComment(comentario.id);
            onChanged({
              ...meme,
              comments: meme.comments.filter((c) => c.id !== comentario.id),
            });
          })
        }
      />

      <p className="meme-actions">
        {/* REPORTAR NO BAJA NADA: lo mira una persona. Por eso desaparece
            cuando ya se reportó — tocarlo dos veces no hace nada y ofrecerlo
            igual hace creer que sí. */}
        {!meme.mine &&
          (meme.reportedByMe ? (
            <span className="help">{t('memes.reported')}</span>
          ) : (
            <button type="button" className="link" onClick={() => setAbriendoReporte(true)}>
              {t('memes.report')}
            </button>
          ))}

        {meme.canRemove && (
          <button
            type="button"
            className="link danger"
            onClick={() =>
              void hacer(async () => {
                await api.removeMeme(meme.id);
                onRemoved();
              })
            }
          >
            {meme.mine ? t('memes.removeMine') : t('memes.remove')}
          </button>
        )}
      </p>

      {/*
        EL CAJÓN DE LA RAZÓN, debajo de los botones y no en una ventana aparte.
        Quien reporta está mirando el meme: taparlo con un diálogo para
        preguntarle por qué lo reporta es quitarle de la vista justo lo que
        tiene que describir.
      */}
      {abriendoReporte && !meme.reportedByMe && (
        <div className="meme-report">
          <label htmlFor={`meme-report-${meme.id}`}>{t('memes.reportWhy')}</label>
          <p className="help">{t('memes.reportWhoReads')}</p>
          <textarea
            id={`meme-report-${meme.id}`}
            rows={2}
            maxLength={MEME_LIMITS.reportNoteMax}
            value={razón}
            placeholder={t('memes.reportWhyPlaceholder')}
            onChange={(e) => setRazón(e.target.value)}
          />
          <div className="row-actions">
            <button
              type="button"
              /* Sin razón el botón no se puede tocar: el servidor la exige, y
                 ofrecer un botón que siempre contesta que no es peor que no
                 ofrecerlo. */
              disabled={reportando || razón.trim().length < MEME_LIMITS.reportNoteMin}
              onClick={() =>
                void hacer(async () => {
                  setReportando(true);
                  try {
                    await api.reportMeme(meme.id, razón);
                    onChanged({ ...meme, reportedByMe: true });
                    setAbriendoReporte(false);
                    setRazón('');
                  } finally {
                    setReportando(false);
                  }
                })
              }
            >
              {t('memes.reportSend')}
            </button>
            <button
              type="button"
              className="link"
              onClick={() => {
                setAbriendoReporte(false);
                setRazón('');
              }}
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </article>
  );
}


/**
 * «LE QUEDAN N HORAS».
 *
 * EN HORAS Y NO EN UNA FECHA: «vence el 21/09 a las 14:32» obliga a hacer una
 * cuenta mental para saber si vale la pena comentar. Y en «menos de una hora»
 * cuando falta poco, porque «quedan 0 horas» se lee como que ya se fue.
 */
function quedan(t: (clave: string, datos?: Record<string, unknown>) => string, iso: string): string {
  const faltan = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(faltan) || faltan <= 0) return t('memes.gone');
  const horas = Math.floor(faltan / 3_600_000);
  return horas < 1 ? t('memes.lessThanHour') : t('memes.hoursLeft', { count: horas });
}

/* MODERAR SE MUDÓ A LA APP DE ADMINISTRACIÓN.

   Acá vivía la cola de reportes, con «bajarlo» y «callar a esta persona». El
   argumento era bueno —bajar un meme pasa mirando el muro, y mandar a quien
   administra a otro lado para borrar lo que está viendo es pedirle dos pasos
   para uno— y aun así estaba mal: aparecía DENTRO de la pantalla donde quien
   administra también publica y comenta como un jugador más, así que se
   revisaba lo de los demás parado en el lugar de quien publica.

   Javier: «yo no puedo callar a nadie, soy un jugador más que administra el
   club». Son dos papeles distintos y ahora están en dos apps distintas: la de
   jugadores solo REPORTA, y quien decide lo hace en la pestaña «Memes
   reportados» de la administración, que se despliega aparte y no va a las
   tiendas.

   El servidor ya lo exigía —`requirePlatformAdmin`— así que esto no cierra
   ningún agujero: quita botones que a casi todo el mundo le contestaban que
   no. Ofrecer un permiso que no se tiene es peor que no ofrecerlo.
*/
